import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { ArrowLeft, Send, Building2, MapPin, Loader2, Upload, FileText, X } from "lucide-react";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function NewApplication({ user }) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'kn' ? 'kn' : 'en';
  
  // Use refs for text inputs to avoid re-renders
  const surveyNumberRef = useRef(null);
  const landAreaRef = useRef(null);
  const purposeRef = useRef(null);
  
  const [department, setDepartment] = useState("revenue");
  
  // Separate state for location
  const [locationData, setLocationData] = useState({
    district: "",
    taluk: "",
    hobli: "",
    village: "",
  });
  
  // Field validation errors
  const [errors, setErrors] = useState({});
  
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Document upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  
  // Cascading dropdown options (fetched from database)
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [hoblis, setHoblis] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState({ districts: false, taluks: false, hoblis: false, villages: false });

  // Helper function to get display name based on language
  const getDisplayName = useCallback((location) => {
    if (!location) return '';
    return currentLang === 'kn' ? location.name_kn : location.name_en;
  }, [currentLang]);

  // Helper function to get display name for selected location values
  const getSelectedDisplayName = useCallback((type, key) => {
    if (!key) return '';
    let list = [];
    switch(type) {
      case 'district':
        list = districts;
        break;
      case 'taluk':
        list = taluks;
        break;
      case 'hobli':
        list = hoblis;
        break;
      case 'village':
        list = villages;
        break;
      default:
        return key;
    }
    const item = list.find(item => item.key === key);
    return item ? getDisplayName(item) : key;
  }, [districts, taluks, hoblis, villages, getDisplayName]);

  // Fetch districts on mount only
  useEffect(() => {
    const fetchDistricts = async () => {
      setLoadingLocations(prev => ({ ...prev, districts: true }));
      try {
        const response = await api.get('/locations/districts?include_all=true');
        setDistricts(response.data);
        // Set default district to Ramanagara (the only active one)
        const activeDistrict = response.data.find(d => d.is_active);
        if (activeDistrict) {
          setLocationData(prev => ({ ...prev, district: activeDistrict.key }));
        }
      } catch (error) {
        console.error('Error fetching districts:', error);
        toast.error(t('newApplication.errorLoadingLocations'));
      } finally {
        setLoadingLocations(prev => ({ ...prev, districts: false }));
      }
    };
    fetchDistricts();
  }, []); // Empty dependency - only run once on mount

  // Fetch taluks when district changes
  useEffect(() => {
    if (locationData.district) {
      const fetchTaluks = async () => {
        setLoadingLocations(prev => ({ ...prev, taluks: true }));
        try {
          const response = await api.get(`/locations/taluks?district=${locationData.district}&include_all=true`);
          setTaluks(response.data);
          // Set default taluk to the first active one (Magadi)
          const activeTaluk = response.data.find(t => t.is_active);
          if (activeTaluk && !locationData.taluk) {
            setLocationData(prev => ({ ...prev, taluk: activeTaluk.key }));
          }
        } catch (error) {
          console.error('Error fetching taluks:', error);
        } finally {
          setLoadingLocations(prev => ({ ...prev, taluks: false }));
        }
      };
      fetchTaluks();
    }
  }, [locationData.district]);

  // Fetch hoblis when taluk changes
  useEffect(() => {
    if (locationData.district && locationData.taluk) {
      const fetchHoblis = async () => {
        setLoadingLocations(prev => ({ ...prev, hoblis: true }));
        try {
          const response = await api.get(`/locations/hoblis?district=${locationData.district}&taluk=${locationData.taluk}`);
          setHoblis(response.data);
        } catch (error) {
          console.error('Error fetching hoblis:', error);
        } finally {
          setLoadingLocations(prev => ({ ...prev, hoblis: false }));
        }
      };
      fetchHoblis();
    }
  }, [locationData.district, locationData.taluk]);

  // Fetch villages when hobli changes
  useEffect(() => {
    if (locationData.district && locationData.taluk && locationData.hobli) {
      const fetchVillages = async () => {
        setLoadingLocations(prev => ({ ...prev, villages: true }));
        try {
          const response = await api.get(`/locations/villages?district=${locationData.district}&taluk=${locationData.taluk}&hobli=${locationData.hobli}`);
          setVillages(response.data);
        } catch (error) {
          console.error('Error fetching villages:', error);
        } finally {
          setLoadingLocations(prev => ({ ...prev, villages: false }));
        }
      };
      fetchVillages();
    }
  }, [locationData.district, locationData.taluk, locationData.hobli]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    // Filter valid file types (PDF, images)
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: ${t('newApplication.invalidFileType')}`);
        return false;
      }
      // Max 10MB per file
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: ${t('newApplication.fileTooLarge')}`);
        return false;
      }
      return true;
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Remove a selected file
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload documents to an application
  const uploadDocuments = async (applicationId) => {
    if (selectedFiles.length === 0) return;
    
    setUploadingDocs(true);
    let successCount = 0;
    
    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        await api.post(`/applications/${applicationId}/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }
    
    setUploadingDocs(false);
    
    if (successCount > 0) {
      toast.success(t('newApplication.documentsUploaded', { count: successCount }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get values from refs
    const land_survey_number = surveyNumberRef.current?.value?.trim() || "";
    const land_area = landAreaRef.current?.value?.trim() || "";
    const purpose = purposeRef.current?.value?.trim() || "";
    
    // Clear previous errors
    const newErrors = {};
    
    // Validate mandatory fields in order (top to bottom)
    if (!land_survey_number) {
      newErrors.surveyNumber = t('newApplication.fieldRequired', { field: t('newApplication.surveyNumber') });
    }
    
    if (!land_area) {
      newErrors.landArea = t('newApplication.fieldRequired', { field: t('newApplication.landArea') });
    }
    
    if (!purpose) {
      newErrors.purpose = t('newApplication.fieldRequired', { field: t('newApplication.purpose') });
    }
    
    if (!locationData.district) {
      newErrors.district = t('newApplication.fieldRequired', { field: t('newApplication.district') });
    }
    
    if (!locationData.taluk) {
      newErrors.taluk = t('newApplication.fieldRequired', { field: t('newApplication.taluk') });
    }
    
    if (!locationData.hobli) {
      newErrors.hobli = t('newApplication.fieldRequired', { field: t('newApplication.hobli') });
    }
    
    if (!locationData.village) {
      newErrors.village = t('newApplication.fieldRequired', { field: t('newApplication.village') });
    }
    
    // Validate that at least one document is selected
    if (selectedFiles.length === 0) {
      newErrors.documents = t('newApplication.fieldRequired', { field: t('newApplication.supportingDocuments') });
    }
    
    // If there are errors, set them and focus on first error field
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Focus on first error field
      if (newErrors.surveyNumber) {
        surveyNumberRef.current?.focus();
      } else if (newErrors.landArea) {
        landAreaRef.current?.focus();
      } else if (newErrors.purpose) {
        purposeRef.current?.focus();
      }
      return;
    }
    
    // Clear errors if validation passes
    setErrors({});
    setLoading(true);

    try {
      const payload = {
        land_survey_number,
        land_area: parseFloat(land_area),
        purpose,
        department,
        ...locationData,
      };
      const response = await api.post("/applications", payload);
      
      // Upload documents if any were selected
      if (selectedFiles.length > 0) {
        await uploadDocuments(response.data.id);
      }
      
      toast.success(t('newApplication.submitSuccess'));
      navigate(`/application/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('newApplication.submitError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 pb-16 md:pb-0">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/farmer/dashboard")}
              className="hover:bg-stone-100"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('application.back')}
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-manrope font-bold text-emerald-700 tracking-tight">
                {t('newApplication.title')}
              </h1>
              <p className="text-sm text-slate-500">{t('newApplication.subtitle')}</p>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-8 bg-white border border-stone-200 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="application-form">
              {/* Department Selection */}
              <div>
                <Label className="text-base font-medium mb-3 block">{t('newApplication.selectDepartment')} *</Label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setDepartment("revenue")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      department === "revenue"
                        ? "border-emerald-600 bg-emerald-50"
                        : "border-stone-200 hover:border-emerald-300"
                    }`}
                    data-testid="dept-revenue"
                  >
                    <Building2 className={`w-8 h-8 mx-auto mb-2 ${department === "revenue" ? "text-emerald-700" : "text-slate-400"}`} />
                    <p className={`font-medium ${department === "revenue" ? "text-emerald-800" : "text-slate-600"}`}>
                      {t('department.revenue')}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{t('newApplication.revenueDesc')}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDepartment("survey")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      department === "survey"
                        ? "border-blue-600 bg-blue-50"
                        : "border-stone-200 hover:border-blue-300"
                    }`}
                    data-testid="dept-survey"
                  >
                    <MapPin className={`w-8 h-8 mx-auto mb-2 ${department === "survey" ? "text-blue-700" : "text-slate-400"}`} />
                    <p className={`font-medium ${department === "survey" ? "text-blue-800" : "text-slate-600"}`}>
                      {t('department.survey')}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{t('newApplication.surveyDesc')}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDepartment("both")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      department === "both"
                        ? "border-purple-600 bg-purple-50"
                        : "border-stone-200 hover:border-purple-300"
                    }`}
                    data-testid="dept-both"
                  >
                    <div className="flex justify-center gap-1 mb-2">
                      <Building2 className={`w-6 h-6 ${department === "both" ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className={`${department === "both" ? "text-purple-600" : "text-slate-400"}`}>→</span>
                      <MapPin className={`w-6 h-6 ${department === "both" ? "text-blue-600" : "text-slate-400"}`} />
                    </div>
                    <p className={`font-medium ${department === "both" ? "text-purple-800" : "text-slate-600"}`}>
                      {t('department.both')}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{t('department.bothDesc')}</p>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="land_survey_number">{t('newApplication.surveyNumber')} *</Label>
                  <Input
                    id="land_survey_number"
                    type="text"
                    ref={surveyNumberRef}
                    className={`mt-1.5 ${errors.surveyNumber ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder={t('newApplication.surveyNumberPlaceholder')}
                    data-testid="input-survey-number"
                    onChange={() => errors.surveyNumber && setErrors(prev => ({ ...prev, surveyNumber: null }))}
                  />
                  {errors.surveyNumber && (
                    <p className="text-red-500 text-xs mt-1" data-testid="error-survey-number">{errors.surveyNumber}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="land_area">{t('newApplication.landArea')} *</Label>
                  <Input
                    id="land_area"
                    type="number"
                    step="0.01"
                    ref={landAreaRef}
                    className={`mt-1.5 ${errors.landArea ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder={t('newApplication.landAreaPlaceholder')}
                    data-testid="input-land-area"
                    onChange={() => errors.landArea && setErrors(prev => ({ ...prev, landArea: null }))}
                  />
                  {errors.landArea && (
                    <p className="text-red-500 text-xs mt-1" data-testid="error-land-area">{errors.landArea}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="purpose">{t('newApplication.purpose')} *</Label>
                <Textarea
                  id="purpose"
                  ref={purposeRef}
                  className={`mt-1.5 ${errors.purpose ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                  rows={3}
                  placeholder={t('newApplication.purposePlaceholder')}
                  data-testid="input-purpose"
                  onChange={() => errors.purpose && setErrors(prev => ({ ...prev, purpose: null }))}
                />
                {errors.purpose && (
                  <p className="text-red-500 text-xs mt-1" data-testid="error-purpose">{errors.purpose}</p>
                )}
              </div>

              {/* Land Location Hierarchy: District → Taluk → Hobli → Village */}
              <div className="border border-stone-200 rounded-lg p-4 bg-stone-50">
                <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-700" />
                  {t('newApplication.landLocation')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* District Dropdown */}
                  <div>
                    <Label htmlFor="district">{t('newApplication.district')} *</Label>
                    <Select
                      value={locationData.district}
                      onValueChange={(value) => {
                        const district = districts.find(d => d.key === value);
                        if (district && district.is_active) {
                          setLocationData({ district: value, taluk: "", hobli: "", village: "" });
                          setHoblis([]);
                          setVillages([]);
                          errors.district && setErrors(prev => ({ ...prev, district: null }));
                        }
                      }}
                      disabled={loadingLocations.districts}
                    >
                      <SelectTrigger className={`mt-1.5 bg-white ${errors.district ? 'border-red-500' : ''}`} data-testid="select-district">
                        {loadingLocations.districts ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.loading', { name: localStorage.getItem('userName') || '' })}...
                          </span>
                        ) : (
                          <SelectValue placeholder={t('newApplication.selectDistrict')} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map((district) => (
                          <SelectItem 
                            key={district.key} 
                            value={district.key}
                            disabled={!district.is_active}
                            className={!district.is_active ? "opacity-50 cursor-not-allowed" : ""}
                          >
                            {getDisplayName(district)}
                            {!district.is_active && <span className="ml-2 text-xs text-muted-foreground">({t('common.comingSoon')})</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.district && (
                      <p className="text-red-500 text-xs mt-1">{errors.district}</p>
                    )}
                  </div>
                  
                  {/* Taluk Dropdown */}
                  <div>
                    <Label htmlFor="taluk">{t('newApplication.taluk')} *</Label>
                    <Select
                      value={locationData.taluk}
                      onValueChange={(value) => {
                        const taluk = taluks.find(t => t.key === value);
                        if (taluk && taluk.is_active) {
                          setLocationData(prev => ({ ...prev, taluk: value, hobli: "", village: "" }));
                          setVillages([]);
                          errors.taluk && setErrors(prev => ({ ...prev, taluk: null }));
                        }
                      }}
                      disabled={!locationData.district || loadingLocations.taluks}
                    >
                      <SelectTrigger className={`mt-1.5 bg-white ${errors.taluk ? 'border-red-500' : ''}`} data-testid="select-taluk">
                        {loadingLocations.taluks ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.loading', { name: localStorage.getItem('userName') || '' })}...
                          </span>
                        ) : (
                          <SelectValue placeholder={locationData.district ? t('newApplication.selectTaluk') : t('newApplication.selectDistrictFirst')} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {taluks.map((taluk) => (
                          <SelectItem 
                            key={taluk.key} 
                            value={taluk.key}
                            disabled={!taluk.is_active}
                            className={!taluk.is_active ? "opacity-50 cursor-not-allowed" : ""}
                          >
                            {getDisplayName(taluk)}
                            {!taluk.is_active && <span className="ml-2 text-xs text-muted-foreground">({t('common.comingSoon')})</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.taluk && (
                      <p className="text-red-500 text-xs mt-1">{errors.taluk}</p>
                    )}
                  </div>
                  
                  {/* Hobli Dropdown */}
                  <div>
                    <Label htmlFor="hobli">{t('newApplication.hobli')} *</Label>
                    <Select
                      value={locationData.hobli}
                      onValueChange={(value) => {
                        setLocationData(prev => ({ ...prev, hobli: value, village: "" }));
                        errors.hobli && setErrors(prev => ({ ...prev, hobli: null }));
                      }}
                      disabled={!locationData.taluk || loadingLocations.hoblis}
                    >
                      <SelectTrigger className={`mt-1.5 bg-white ${errors.hobli ? 'border-red-500' : ''}`} data-testid="select-hobli">
                        {loadingLocations.hoblis ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.loading', { name: localStorage.getItem('userName') || '' })}...
                          </span>
                        ) : (
                          <SelectValue placeholder={locationData.taluk ? t('newApplication.selectHobli') : t('newApplication.selectTalukFirst')} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {hoblis.map((hobli) => (
                          <SelectItem key={hobli.key} value={hobli.key}>
                            {getDisplayName(hobli)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.hobli && (
                      <p className="text-red-500 text-xs mt-1">{errors.hobli}</p>
                    )}
                  </div>
                  
                  {/* Village Dropdown */}
                  <div>
                    <Label htmlFor="village">{t('newApplication.village')} *</Label>
                    <Select
                      value={locationData.village}
                      onValueChange={(value) => {
                        setLocationData(prev => ({ ...prev, village: value }));
                        errors.village && setErrors(prev => ({ ...prev, village: null }));
                      }}
                      disabled={!locationData.hobli || loadingLocations.villages}
                    >
                      <SelectTrigger className={`mt-1.5 bg-white ${errors.village ? 'border-red-500' : ''}`} data-testid="select-village">
                        {loadingLocations.villages ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.loading', { name: localStorage.getItem('userName') || '' })}...
                          </span>
                        ) : (
                          <SelectValue placeholder={locationData.hobli ? t('newApplication.selectVillage') : t('newApplication.selectHobliFirst')} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {villages.map((village) => (
                          <SelectItem key={village.key} value={village.key}>
                            {getDisplayName(village)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.village && (
                      <p className="text-red-500 text-xs mt-1">{errors.village}</p>
                    )}
                  </div>
                </div>
                
                {/* Location Summary */}
                {locationData.village && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                    <p className="text-xs text-emerald-700 font-medium mb-1">{t('newApplication.selectedLocation')}:</p>
                    <p className="text-sm text-emerald-900">
                      {getSelectedDisplayName('village', locationData.village)}, {getSelectedDisplayName('hobli', locationData.hobli)} {t('newApplication.hobliSuffix')}, {getSelectedDisplayName('taluk', locationData.taluk)} {t('newApplication.talukSuffix')}, {getSelectedDisplayName('district', locationData.district)}
                    </p>
                  </div>
                )}
              </div>

              {/* Supporting Documents Section */}
              <div className={`border rounded-lg p-4 bg-stone-50 ${errors.documents ? 'border-red-500' : 'border-stone-200'}`}>
                <h3 className="text-sm font-semibold text-emerald-700 mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-700" />
                  {t('newApplication.supportingDocuments')} *
                </h3>
                <p className="text-xs text-slate-500 mb-4">{t('newApplication.documentsHint')}</p>
                
                {/* Upload Area */}
                <Label
                  htmlFor="document-upload"
                  className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer hover:border-emerald-500 transition-colors bg-white ${errors.documents ? 'border-red-400' : 'border-stone-300'}`}
                  data-testid="document-upload-area"
                >
                  <div className="flex flex-col items-center justify-center py-4">
                    <Upload className="w-6 h-6 mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600 font-medium">{t('newApplication.clickToUpload')}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('newApplication.supportedFormats')}</p>
                  </div>
                  <input
                    id="document-upload"
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      handleFileSelect(e);
                      errors.documents && setErrors(prev => ({ ...prev, documents: null }));
                    }}
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    data-testid="document-file-input"
                  />
                </Label>
                
                {/* Error message for documents */}
                {errors.documents && (
                  <p className="text-red-500 text-xs mt-2">{errors.documents}</p>
                )}
                
                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="selected-files-list">
                    <p className="text-xs text-slate-600 font-medium">
                      {t('newApplication.selectedFiles')} ({selectedFiles.length})
                    </p>
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white border border-stone-200 rounded-lg"
                        data-testid={`selected-file-${index}`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <span className="text-sm text-slate-700 truncate">{file.name}</span>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFile(index)}
                          className="text-slate-400 hover:text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                          data-testid={`remove-file-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-stone-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/farmer/dashboard")}
                  className="bg-white hover:bg-stone-50"
                  data-testid="cancel-button"
                >
                  {t('newApplication.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading || uploadingDocs}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white shadow-sm transition-all active:scale-95"
                  data-testid="submit-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {uploadingDocs ? t('newApplication.uploadingDocuments') : t('newApplication.submitting')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {t('newApplication.submit')}
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}